import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthenticatedMediaUrl } from "@/hooks/useAuthenticatedMedia";
import ItemRating, { type ItemRatingValue } from "./ItemRating";
import { Badge } from "@/components/ui/badge";

export interface LightboxEntry {
  url: string;
  isVideo?: boolean;
  item?: {
    name: string;
    price: number;
    itemRating: ItemRatingValue;
    itemRatingText?: string;
    isTheBest: boolean;
  } | null;
}

interface MediaLightboxProps {
  entries: LightboxEntry[];
  initialIndex?: number;
  open: boolean;
  onClose: () => void;
}

function LightboxImage({ src, alt }: { src: string; alt: string }) {
  const resolved = useAuthenticatedMediaUrl(src) ?? src;
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  return (
    // Wrapper is pointer-events-none so clicks on letterboxing area reach root.
    // The <img> itself is pointer-events-auto + stopPropagation so tapping
    // the actual image does NOT close the lightbox.
    <div className="relative w-full h-full flex items-center justify-center pointer-events-none">
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-10 h-10 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}
      <img
        src={resolved}
        alt={alt}
        className={cn(
          "max-w-full max-h-full object-contain transition-opacity duration-300 select-none pointer-events-auto",
          loaded ? "opacity-100" : "opacity-0"
        )}
        onLoad={() => setLoaded(true)}
        onClick={(e) => e.stopPropagation()}
        draggable={false}
      />
    </div>
  );
}

export function MediaLightbox({ entries, initialIndex = 0, open, onClose }: MediaLightboxProps) {
  const [index, setIndex] = useState(initialIndex);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const photoEntries = entries.filter(e => !e.isVideo);
  const total = photoEntries.length;
  const current = photoEntries[index] ?? null;

  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  const prev = useCallback(() => setIndex(i => Math.max(0, i - 1)), []);
  const next = useCallback(() => setIndex(i => Math.min(total - 1, i + 1)), [total]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose, prev, next]);

  if (!mounted || !current) return null;

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    touchStartX.current = null;
    touchStartY.current = null;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
      if (dx < 0) next();
      else prev();
    } else if (dy > 80 && Math.abs(dx) < 40) {
      onClose();
    }
  };

  const item = current.item ?? null;

  // Portal to document.body so the overlay escapes ancestor stacking contexts
  // and CSS transform containment (ReviewCard's hover-elevate + Ken Burns).
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      className={cn(
        "fixed inset-0 z-[9999] bg-black flex flex-col transition-opacity duration-200",
        visible ? "opacity-100" : "opacity-0"
      )}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onClick={onClose}
    >
      {/* Top bar: close + counter — stops propagation so its clicks don't close */}
      <div
        className="relative flex items-center justify-between px-4 pt-safe-top pt-4 pb-2 flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="flex items-center justify-center w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm text-white"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {total > 1 && (
          <span className="text-white/80 text-sm font-medium">
            {index + 1} / {total}
          </span>
        )}

        <div className="w-9" />
      </div>

      {/* Image area — all clicks close (including letterboxing).
          Arrow buttons individually stop propagation so they navigate instead. */}
      <div className="flex-1 relative flex items-center justify-center min-h-0 px-2">
        <LightboxImage src={current.url} alt={item?.name ?? "Review photo"} />

        {/* Prev arrow — stops propagation so click doesn't close */}
        {index > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm text-white"
            aria-label="Previous photo"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}

        {/* Next arrow — stops propagation so click doesn't close */}
        {index < total - 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm text-white"
            aria-label="Next photo"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Bottom area: item overlay + dot indicators — stops propagation */}
      <div
        className="flex-shrink-0 pb-safe-bottom pb-6 px-4 pt-3 flex flex-col gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        {item && (
          <div className="bg-black/70 backdrop-blur-sm rounded-md px-3 py-2 self-start max-w-full">
            <div className="flex items-baseline gap-1.5">
              <div className="text-white text-sm font-medium">{item.name}</div>
              {item.price > 0 && (
                <div className="text-white/75 text-xs flex-shrink-0">${item.price.toFixed(2)}</div>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-1">
              <ItemRating rating={item.itemRating} size="sm" />
              {item.itemRatingText && (
                <div className="text-white/90 text-xs italic truncate max-w-[200px]">
                  "{item.itemRatingText}"
                </div>
              )}
              {item.isTheBest && (
                <Badge
                  variant="secondary"
                  className="text-xs h-5 bg-yellow-500/90 text-black border-0 flex-shrink-0"
                >
                  The Best
                </Badge>
              )}
            </div>
          </div>
        )}

        {total > 1 && (
          <div className="flex justify-center gap-1.5">
            {photoEntries.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setIndex(i); }}
                aria-label={`Go to photo ${i + 1}`}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === index ? "w-4 bg-white" : "w-1.5 bg-white/50"
                )}
              />
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
